// src/pages/StaffFormModal.jsx
import React, { useState, useEffect } from "react";
import { X, ChevronDown, Loader2, AlertCircle, Phone, Mail } from "lucide-react";
import { clsx } from "clsx";

const DEPT_OPTIONS = ["서울위원", "경기위원"];
const POS_OPTIONS  = ["팀장", "수행요원", "수습요원"];

const EMPTY_FORM = { staffId: "", staffNm: "", deptNm: "서울위원", positionNm: "수행요원", telNo: "", email: "", useYn: "Y" };

export function StaffFormModal({ type, staff, onClose, onSubmit, isSubmitting }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (type === "edit" && staff) setForm(staff);
    else setForm(EMPTY_FORM);
  }, [type, staff]);

  const setF = (key, value) => {
    setForm(p => ({ ...p, [key]: value }));
    setErrors(p => { const n = { ...p }; delete n[key]; return n; });
  };

  const validate = () => {
    const err = {};
    if (type === "add" && !form.staffId.trim()) err.staffId = "사번/ID를 입력하세요.";
    if (!form.staffNm.trim()) err.staffNm = "성명을 입력하세요.";
    if (form.telNo && !/^[\d\-]+$/.test(form.telNo)) err.telNo = "올바른 연락처 형식이 아닙니다.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "올바른 이메일 형식이 아닙니다.";
    return err;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    try {
      if (type === "add") await onSubmit(form);
      else await onSubmit({ staffId: form.staffId, payload: form });
      onClose();
    } catch (e) {
      alert(e?.response?.data?.message || "오류가 발생했습니다.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="font-bold text-slate-800">{type === "add" ? "수행요원 신규 등록" : "수행요원 정보 수정"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">사번/ID <span className="text-red-400">*</span></label>
              <input type="text" value={form.staffId} onChange={(e) => setF("staffId", e.target.value)} readOnly={type === "edit"} placeholder="예: STF-013" className={clsx("h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-1", errors.staffId ? "border-red-400 focus:ring-red-200" : "border-slate-300 focus:ring-orange-200", type === "edit" && "bg-slate-50 cursor-not-allowed")} />
              {errors.staffId && <p className="mt-1 flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" />{errors.staffId}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">성명 <span className="text-red-400">*</span></label>
              <input type="text" value={form.staffNm} onChange={(e) => setF("staffNm", e.target.value)} placeholder="홍길동" className={clsx("h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-1", errors.staffNm ? "border-red-400 focus:ring-red-200" : "border-slate-300 focus:ring-orange-200")} />
              {errors.staffNm && <p className="mt-1 flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" />{errors.staffNm}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">소속 부서</label>
              <select value={form.deptNm} onChange={(e) => setF("deptNm", e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm">
                {DEPT_OPTIONS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">직급/직책</label>
              <select value={form.positionNm} onChange={(e) => setF("positionNm", e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm">
                {POS_OPTIONS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">연락처</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input type="text" value={form.telNo} onChange={(e) => setF("telNo", e.target.value)} placeholder="010-0000-0000" className={clsx("h-10 w-full rounded-lg border pl-9 pr-3 text-sm focus:outline-none focus:ring-1", errors.telNo ? "border-red-400" : "border-slate-300")} />
              </div>
              {errors.telNo && <p className="mt-1 flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" />{errors.telNo}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">이메일</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} placeholder="user@company.com" className={clsx("h-10 w-full rounded-lg border pl-9 pr-3 text-sm focus:outline-none focus:ring-1", errors.email ? "border-red-400" : "border-slate-300")} />
              </div>
              {errors.email && <p className="mt-1 flex items-center gap-1 text-xs text-red-500"><AlertCircle className="h-3 w-3" />{errors.email}</p>}
            </div>
          </div>
          
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-200 mt-6">
            <button type="button" onClick={onClose} className="h-10 px-5 rounded-lg border bg-white text-sm">취소</button>
            <button type="submit" disabled={isSubmitting} className="h-10 flex items-center gap-2 px-5 rounded-lg bg-orange-500 text-white text-sm hover:bg-orange-600 disabled:opacity-50">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} {type === "add" ? "등록" : "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}